import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUpdateMyProfile } from "@/api/mutations";
import { useGetMyAccounts } from "@/api/queries";
import { getDefaultAccount, setDefaultAccount } from "@/store/appStateZustand";

import type { UseSettingsMutationProps, UseSettingsMutationReturn } from "../types/settings.types";
import type { SettingsFormValues } from "../utils/settingsSchema";

export default function useSettingsMutation({
  form,
}: UseSettingsMutationProps): UseSettingsMutationReturn {
  const { t } = useTranslation();
  const myAccounts = useGetMyAccounts();
  const updateMutation = useUpdateMyProfile();

  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [defaultAccountCode, setDefaultAccountCodeState] = useState<string | null>(() =>
    getDefaultAccount(),
  );

  const accounts = myAccounts?.data?.artistaccounts ?? [];
  const hasMultipleAccounts = accounts.length > 1;
  const defaultAccountDisplay = (() => {
    const acc = accounts.find(a => a.artistaccountcode === defaultAccountCode);
    return acc ? `${acc.accountname} (${acc.artistaccountcode})` : null;
  })();

  const handleUpdateSettings = async (data: SettingsFormValues): Promise<void> => {
    setPending(true);
    setMutationErrorMessage("");
    form.clearErrors("root");
    updateMutation.reset();
    try {
      const response = await updateMutation.mutateAsync(data);
      if (response.failedvalidation) {
        form.setError("root", { message: String(response.data?.messages ?? "") });
        return;
      }
      toast.success(t("settings:profile-updated-toast", "Profile updated"));
    }
    catch (error) {
      setMutationErrorMessage(error instanceof Error ? error.message : String(error));
    }
    finally {
      setPending(false);
    }
  };

  const handleSetDefaultAccountCode = (code: string | null) => {
    const resolved = code === "" ? null : code;
    setDefaultAccount(resolved);
    setDefaultAccountCodeState(resolved);

    if (resolved !== null) {
      const selectedAcc = accounts.find(acc => acc.artistaccountcode === resolved);
      toast.success(t("settings:default-account-updated", "Default account updated"), {
        description: selectedAcc
          ? `${selectedAcc.accountname} (${selectedAcc.artistaccountcode})`
          : resolved,
        duration: 3000,
      });
    }
    else {
      toast.success(t("settings:default-account-cleared", "Default account cleared"), {
        duration: 3000,
      });
    }
  };

  return {
    handleUpdateSettings,
    handleSetDefaultAccountCode,
    defaultAccountCode,
    hasMultipleAccounts,
    defaultAccountDisplay,
    accounts,
    pending,
    mutationErrorMessage,
  };
}
